import axios from "axios";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Session lives in HttpOnly cookies (set by /auth/login, /auth/refresh, /mfa/verify-login) — nothing is kept in localStorage.
try { localStorage.removeItem("access_token"); localStorage.removeItem("refresh_token"); } catch {}

// One refresh at a time: a burst of 401s shares the same in-flight POST /auth/refresh.
let refreshing = null;
export function refreshSession() {
  if (!refreshing) {
    refreshing = axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}
API.interceptors.request.use((config) => {
  // Let browser set correct Content-Type for FormData (multipart/form-data with boundary)
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

// Response interceptor: handle 401 with token refresh
API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Offline queue: if the request hit a network error and the endpoint is safe
    // to replay later, enqueue it transparently and resolve with a queued receipt.
    // This keeps field-worker / admin flows responsive when the network drops.
    const isNetworkErr = !error.response || error.code === "ERR_NETWORK" || error.message === "Network Error";
    if (isNetworkErr && !originalRequest?._queued && !originalRequest?._isFlushed) {
      // Lazy import avoids a circular reference at module load time
      // (api.js ← offlineQueue.js)
      try {
        // eslint-disable-next-line global-require
        const queue = require("./offlineQueue");
        const rawUrl = (originalRequest.baseURL || "") + (originalRequest.url || "");
        if (queue.isQueueable(originalRequest.method, rawUrl) && !(originalRequest.data instanceof FormData)) {
          const payload = typeof originalRequest.data === "string"
            ? tryParseJSON(originalRequest.data)
            : originalRequest.data;
          queue.enqueue({
            method: originalRequest.method,
            url: originalRequest.url,
            data: payload,
            label: originalRequest.headers?.["X-Queue-Label"] || deriveLabel(originalRequest),
          });
          // Resolve with a synthetic 202 Accepted so callers continue gracefully.
          return Promise.resolve({
            status: 202,
            statusText: "Queued Offline",
            data: { queued: true, message: "Saved offline — will sync when reconnected" },
            headers: {},
            config: originalRequest,
          });
        }
      } catch { /* fall through to normal rejection */ }
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !/\/auth\/(login|refresh|logout|register|tenant-signup)|\/mfa\/verify-login/.test(originalRequest.url || "")
    ) {
      originalRequest._retry = true;
      try {
        await refreshSession();
        return API(originalRequest);
      } catch {
        // No valid refresh cookie: the app-load session probe (/auth/me) and anything on the login page just fails
        // quietly — redirecting here would reload /login forever for a logged-out visitor.
        if (/\/auth\/me(\?|$)/.test(originalRequest.url || "") || window.location.pathname.startsWith("/login")) {
          return Promise.reject(error);
        }
        const slug = localStorage.getItem("portal_slug");
        window.location.href = slug ? `/login/${slug}` : "/login";
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

function tryParseJSON(s) {
  try { return JSON.parse(s); } catch { return {}; }
}

function deriveLabel(config) {
  const url = (config.url || "").toLowerCase();
  if (url.includes("/en-route")) return "Mark en route";
  if (url.includes("/arrived")) return "Mark as arrived";
  if (url.includes("/start")) return "Start task";
  if (url.includes("/complete")) return "Complete task";
  if (url === "/tasks") return "Create task";
  if (url === "/jobs") return "Create job";
  if (url === "/time-logs") return "Time log";
  return "Queued action";
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (detail?.isAxiosError || detail?.response) return formatApiError(detail.response?.data?.detail ?? detail.message);
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default API;
